// Action panels for the application, review, approval, supporting-document and
// authorisation commands. Keys are command names (src/features/contracts.ts).
// Every panel states what the signer declares, sends business input only (the
// server sets signer and time), and shows every server blocker with its rule ids.
import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateInput, DateTimeInput } from '@/components/DateTimeInput'
import { BlockerList, ErrorBanner, PermitChangedBanner, RuleRefs, SignatureLine } from '@/components/evidence'
import { Badge, Banner, Button, CheckList, Field, KeyValue, RadioGroup, Select, TextArea, TextInput } from '@/components/ui'
import { MEETING_CHECKS, ROLES } from '@/domain/catalogue'
import type { AvailableAction, PermitBundle, RevisionRequirement, RevisionView } from '@/domain/types'
import type { ActionPanelMap, ActionPanelProps } from '@/features/contracts'
import { PtwError } from '@/lib/commands'
import { fmtDate, fmtDateTime, fromLocalInput, inputTimeArg, shortDigest, toLocalInput } from '@/lib/format'
import { type SeenVersion, useMe, usePolicy, useRegistry, useSeenVersion } from '@/lib/queries'
import { Declaration, PolicyNote, StatusBadge, SubHeading, TextBlock, YesNo } from './bits'
import { useGuardedCommand, useServerClock, useServerNow } from './hooks'
import {
  argString,
  AUTHORISE_DECLARATION,
  findParam,
  groupBlockersByStep,
  latestReturn,
  normaliseDraftFields,
  paramNumber,
  PROCEDURE_STATUS,
  RECORD_STATUS,
  sealedLocation,
  stepInfo,
  SUBMIT_DECLARATION,
  verifierRolesFor,
  windowArg,
  windowSpan,
  yesNo,
} from './model'
import { applicationDates, AreaClassificationView, ClassActivitiesList, ControlsList, makeLookup, procedureText, taskFromRevision, ThirdPartyView, WindowText } from './views'

type SupportingRecord = PermitBundle['supporting_records'][number]
type DocRequirement = Extract<RevisionRequirement, { requirement_kind: 'supporting_document' }>
type OacRequirement = Extract<RevisionRequirement, { requirement_kind: 'oac_agreement' }>

// -----------------------------------------------------------------------------
// Shared pieces
// -----------------------------------------------------------------------------
function NotAvailable({ action }: { action: AvailableAction }) {
  if (action.enabled) return null
  return (
    <Banner tone="warn" title="The server lists this step as not available to you now">
      {action.reason ?? 'No reason was given.'} You can still try: the server checks again and lists everything that blocks it.
    </Banner>
  )
}

function PanelFrame({ action, children, declaration, declared, onDeclared, missing, error, errorView, pending, signLabel, onSign, onCancel, danger, seen }: {
  action: AvailableAction
  children: ReactNode
  declaration: ReactNode
  declared: boolean
  onDeclared: (v: boolean) => void
  missing: string[]
  error: unknown
  errorView?: ReactNode
  pending: boolean
  signLabel: string
  onSign: () => void
  onCancel: () => void
  danger?: boolean
  /** The version the signer was shown (versioned commands); a change must be acknowledged first. */
  seen?: SeenVersion
}) {
  const mustAcknowledge = Boolean(seen?.changed)
  return (
    <div className="space-y-4">
      <NotAvailable action={action} />
      {seen?.changed ? <PermitChangedBanner changes={seen.changes} onAcknowledge={seen.acknowledge} /> : null}
      {children}
      <Declaration text={declaration} checked={declared} onChange={onDeclared} />
      {missing.length > 0 ? (
        <p className="text-sm text-ink">
          <span className="font-semibold">Still needed before you can sign: </span>
          {missing.join('; ')}.
        </p>
      ) : null}
      {mustAcknowledge ? <p className="text-sm font-semibold text-ink">Confirm that you have checked the changes (at the top) before you sign.</p> : null}
      {errorView ?? <ErrorBanner error={error} />}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onSign} loading={pending} disabled={!declared || missing.length > 0 || mustAcknowledge}>
          {signLabel}
        </Button>
      </div>
    </div>
  )
}

/**
 * Runs a command; on success closes the panel. Failures stay visible through the command's
 * error. A stale version (PW002) re-reads the permit so the change is shown for acknowledgement.
 */
async function attempt(run: () => Promise<unknown>, onDone: () => void, seen?: SeenVersion): Promise<void> {
  try {
    await run()
    onDone()
  } catch (e) {
    if (seen && PtwError.from(e).code === 'PW002') seen.reread()
  }
}

function revisionFor(bundle: PermitBundle, action: AvailableAction): RevisionView | null {
  const id = argString(action.args, 'revision_id') ?? bundle.permit.current_revision_id
  return bundle.revisions.find((r) => r.id === id) ?? null
}

function MissingRevision({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="space-y-4">
      <Banner tone="bad" title="The revision for this step is not available">
        Close this and refresh the permit. If it stays missing, the permit has no sealed revision you can see.
      </Banner>
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onCancel}>
          Close
        </Button>
      </div>
    </div>
  )
}

function RevisionLine({ revision, bundle }: { revision: RevisionView; bundle: PermitBundle }) {
  const current = revision.id === bundle.permit.current_revision_id
  return (
    <p className="text-sm text-ink-soft">
      Revision {revision.revision_number}
      {current ? '' : ' (not the current revision)'}, sealed {fmtDateTime(revision.sealed_at)} by {revision.submission.signer.display_name}. Digest{' '}
      <span className="code">#{shortDigest(revision.scope_digest)}</span>.
    </p>
  )
}

function docRequirements(revision: RevisionView): DocRequirement[] {
  return revision.requirements.filter((r): r is DocRequirement => r.requirement_kind === 'supporting_document').sort((a, b) => a.sequence - b.sequence)
}

function oacRequirements(revision: RevisionView): OacRequirement[] {
  return revision.requirements.filter((r): r is OacRequirement => r.requirement_kind === 'oac_agreement').sort((a, b) => a.sequence - b.sequence)
}

function recordLabel(r: SupportingRecord): string {
  return `${r.record_kind}: ${r.external_identifier} revision ${r.external_revision} - ${RECORD_STATUS[r.status]?.label ?? r.status}`
}

function RecordSummary({ record }: { record: SupportingRecord }) {
  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-ink">{record.record_kind}</p>
        <StatusBadge status={RECORD_STATUS[record.status] ?? { label: record.status, tone: 'neutral' }} />
      </div>
      <KeyValue
        items={[
          ['Identifier', record.external_identifier],
          ['Revision', record.external_revision],
          ['Issued by', record.issuer],
          ['Issued', record.reported_issued_at ? fmtDate(record.reported_issued_at) : 'Not stated'],
          ['Expires', record.reported_expires_at ? fmtDate(record.reported_expires_at) : 'Not stated'],
          ['Scope note', <TextBlock text={record.scope_note} empty="None" />],
          ['Evidence reference', <TextBlock text={record.evidence_reference} empty="None" />],
        ]}
      />
      <div className="mt-2">
        <SignatureLine a={record.recorded} label="Entered by" />
      </div>
    </div>
  )
}

function ScopeSummary({ revision }: { revision: RevisionView }) {
  const c = normaliseDraftFields(revision.content)
  return (
    <details className="rounded-lg border border-line bg-white p-3">
      <summary className="tap cursor-pointer py-2 text-sm font-semibold text-brand-dark">Read the scope and controls of revision {revision.revision_number}</summary>
      <div className="mt-2 space-y-2 text-sm">
        <KeyValue
          items={[
            ['Title', <TextBlock text={c.title} />],
            ['Work order reference', <TextBlock text={c.work_order_ref} empty="None" />],
            ['Unit', `${revision.site_area.name} (${revision.site_area.code})`],
            ['Why this class: the activities in this work', <ClassActivitiesList codes={c.class_activities} sealed={revision.content.class_activities_detail} />],
            ['Exact location', <TextBlock text={c.location_detail} />],
            ...applicationDates(revision.submission.server_signed_at ?? revision.sealed_at, c.requested_from),
            ['Requested window', <WindowText from={c.requested_from} to={c.requested_to} />],
            ['What will be done', <TextBlock text={c.work_description} />],
            ['Work boundary', <TextBlock text={c.work_boundary} />],
            ['JHSEP reference', <TextBlock text={revision.jhsep?.reference ?? null} empty="None" />],
          ]}
        />
        <SubHeading>Area classification (as sealed)</SubHeading>
        <AreaClassificationView code={sealedLocation(revision.content)?.code ?? null} label={sealedLocation(revision.content)?.label ?? null} register={revision.area_classification} />
        {typeof revision.content.third_party === 'object' && revision.content.third_party !== null ? (
          <>
            <SubHeading>Third party or subcontractor</SubHeading>
            <ThirdPartyView tp={c.third_party} />
          </>
        ) : null}
        <SubHeading>Hazards and controls</SubHeading>
        <ControlsList items={[...revision.controls].sort((a, b) => a.sequence - b.sequence).map((x) => ({ hazard: x.hazard, control: x.control, kind: x.control_kind }))} />
      </div>
    </details>
  )
}

const nonBlank = (s: string | null | undefined): boolean => Boolean(s && s.trim())

// -----------------------------------------------------------------------------
// revise_permit_draft: opens the editor
// -----------------------------------------------------------------------------
function ReviseDraftPanel({ bundle, action, onCancel }: ActionPanelProps) {
  const navigate = useNavigate()
  const returned = latestReturn(bundle)
  return (
    <div className="space-y-4">
      <NotAvailable action={action} />
      <p className="text-sm text-ink">Change the application in the step-by-step editor. Saving a draft signs nothing; you sign when you submit it.</p>
      {returned ? (
        <Banner tone="warn" title="Returned for revision">
          {returned.attestation.signer.display_name}
          {returned.change_disposition ? ` (${returned.change_disposition} change)` : ''}: {returned.reason ?? 'no reason recorded'}
        </Banner>
      ) : null}
      {bundle.draft ? (
        <p className="text-xs text-ink-soft">
          Draft last saved {fmtDateTime(bundle.draft.updated_at)} by {bundle.draft.updated_by.display_name}.
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => navigate(`/permits/${bundle.permit.id}/edit`)}>Open the application editor</Button>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// submit_permit
// -----------------------------------------------------------------------------
function SubmitPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const navigate = useNavigate()
  const registryQ = useRegistry()
  const cmd = useGuardedCommand('submit_permit')
  const seen = useSeenVersion(bundle)
  const [declared, setDeclared] = useState(false)
  const draft = bundle.draft
  const f = draft ? normaliseDraftFields(draft.fields) : null
  const lookup = makeLookup(registryQ.data, bundle)
  const ph = f ? lookup.person(f.nominated_ph_person_id) : null
  const err = cmd.error
  const blockers = err?.code === 'PW001' ? err.blockers : []
  const editTo = (step?: string) => `/permits/${bundle.permit.id}/edit${step ? `?step=${step}` : ''}`
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={SUBMIT_DECLARATION}
      declared={declared}
      onDeclared={setDeclared}
      missing={draft ? [] : ['a saved draft you can see']}
      error={err}
      errorView={
        blockers.length > 0 ? (
          <Banner tone="warn" title={`Not submitted: ${blockers.length} thing${blockers.length === 1 ? '' : 's'} to fix`}>
            <p className="text-xs text-ink-soft">Nothing was signed or sealed.</p>
            {groupBlockersByStep(blockers).map((g) => (
              <div key={g.step ?? 'general'} className="mt-3">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-ink">{g.step ? `Step ${stepInfo(g.step).no}: ${stepInfo(g.step).title}` : 'The whole application'}</p>
                  <Button variant="secondary" onClick={() => navigate(editTo(g.step ?? 'review'))}>
                    Fix in the editor
                  </Button>
                </div>
                <BlockerList blockers={g.blockers} />
              </div>
            ))}
          </Banner>
        ) : undefined
      }
      pending={cmd.isPending}
      signLabel="Sign and submit"
      onCancel={onCancel}
      onSign={() => void attempt(() => cmd.run({ permit_id: bundle.permit.id }, seen.version), onDone, seen)}
    >
      <p className="text-sm text-ink">
        This seals the saved draft as a new revision and sends it to the Responsible Supervisor for the permit meeting. It does not authorise any work. Unsaved
        changes in an open editor are not included.
      </p>
      {draft && f ? (
        <div className="rounded-lg border border-line bg-white p-3">
          <p className="mb-2 text-xs text-ink-soft">
            Saved draft from {fmtDateTime(draft.updated_at)} by {draft.updated_by.display_name}
          </p>
          <KeyValue
            items={[
              ['Title', <TextBlock text={f.title} />],
              ['Unit', lookup.area(f.site_area_id)?.name ?? <span className="text-ink-faint">Not chosen</span>],
              ['Planned window', <WindowText from={f.requested_from} to={f.requested_to} />],
              ['Tasks', String(f.tasks.length)],
              ['Nominated Permit Holder', ph?.display_name ?? <span className="text-ink-faint">Not chosen</span>],
              ['Gas testing required', yesNo(f.gas_plan.required)],
            ]}
          />
        </div>
      ) : (
        <Banner tone="warn">The saved draft is not visible to you.</Banner>
      )}
      <Button variant="secondary" onClick={() => navigate(editTo('review'))}>
        Review the full application first
      </Button>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// record_meeting_review (RS)
// -----------------------------------------------------------------------------
// The historic Area Authority (before 0016) is not a role anyone attends a meeting in now.
const ROLE_SUGGESTIONS = [...Object.values(ROLES).filter((r) => r !== ROLES.AA).map((r) => r.label), 'Area planner', 'Maintenance discipline supervisor']

function MeetingReviewPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const me = useMe()
  const cmd = useGuardedCommand('record_meeting_review')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const [checklist, setChecklist] = useState<Record<string, boolean>>({})
  const [planning, setPlanning] = useState<'frozen_plan' | 'urgent_justified' | null>(null)
  const [participants, setParticipants] = useState<{ name: string; role: string }[]>([{ name: '', role: '' }])
  const [visit, setVisit] = useState<boolean | null>(null)
  const [notes, setNotes] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />

  const c = normaliseDraftFields(revision.content)
  const ticked = MEETING_CHECKS.filter((x) => checklist[x.code]).length
  const complete = participants.filter((p) => nonBlank(p.name) && nonBlank(p.role))
  const partial = participants.some((p) => nonBlank(p.name) !== nonBlank(p.role))
  const missing: string[] = []
  if (ticked < MEETING_CHECKS.length) missing.push(`all ${MEETING_CHECKS.length} meeting checks confirmed (${MEETING_CHECKS.length - ticked} not yet)`)
  if (!planning) missing.push('the planning basis')
  if (visit === null) missing.push('whether an RS visit is needed before authorisation')
  if (complete.length === 0) missing.push('at least one participant with a name and role')
  if (partial) missing.push('a name and a role for every participant listed')

  const quick: { name: string; role: string }[] = []
  if (me.data) quick.push({ name: me.data.person.display_name, role: ROLES[action.role as keyof typeof ROLES]?.label ?? action.role })
  for (const p of revision.parties) quick.push({ name: p.person.display_name, role: p.role_code === 'PA' ? ROLES.PA.label : ROLES.PH.label })
  const quickAdds = quick.filter(
    (q, i) => quick.findIndex((x) => x.name === q.name) === i && !participants.some((p) => p.name.trim() === q.name),
  )

  const addParticipant = (p: { name: string; role: string }) =>
    setParticipants((list) => {
      const blank = list.findIndex((x) => !nonBlank(x.name) && !nonBlank(x.role))
      return blank >= 0 ? list.map((x, i) => (i === blank ? p : x)) : [...list, p]
    })

  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={`I confirm the permit meeting reviewed revision ${revision.revision_number} of this application and covered each check I ticked. This review does not authorise any work.`}
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign the review"
      onCancel={onCancel}
      onSign={() => {
        if (!planning || visit === null) return // never send a default for an unanswered question
        void attempt(
          () =>
            cmd.run(
              {
                permit_id: bundle.permit.id,
                revision_id: revision.id,
                planning_basis: planning,
                checklist: Object.fromEntries(MEETING_CHECKS.map((x) => [x.code, Boolean(checklist[x.code])])),
                participants: complete,
                rs_pre_authorisation_visit_required: visit,
                notes: notes.trim() ? notes : null,
              },
              seen.version,
            ),
          onDone,
          seen,
        )
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <ScopeSummary revision={revision} />

      <div>
        <SubHeading aside={<Badge tone={ticked === MEETING_CHECKS.length ? 'ok' : 'warn'}>{`${ticked} of ${MEETING_CHECKS.length} confirmed`}</Badge>}>
          Meeting checks
        </SubHeading>
        <p className="mb-2 text-xs text-ink-soft">
          Tick each check the meeting confirmed. All of them are required. If the meeting could not confirm one, return the application instead.
          <RuleRefs ruleIds={['CORE-045']} />
        </p>
        <CheckList options={MEETING_CHECKS} value={checklist} onChange={setChecklist} />
      </div>

      <div>
        <SubHeading>Planning basis</SubHeading>
        {nonBlank(c.urgency_justification) ? (
          <Banner tone="warn" className="mb-2" title="The applicant gave an urgency justification">
            <TextBlock text={c.urgency_justification} />
            <p className="mt-1 text-xs">Choose "urgent" only if the meeting accepts this justification. If it does not, return the application instead.</p>
          </Banner>
        ) : null}
        <RadioGroup
          name={`${id}-planning`}
          value={planning}
          onChange={setPlanning}
          options={[
            { value: 'frozen_plan', label: 'In the frozen plan', hint: 'The job is in the current frozen plan and the notice is adequate.' },
            { value: 'urgent_justified', label: 'Urgent - justification accepted', hint: 'Short notice, and the meeting accepted the reason (CORE-016).' },
          ]}
        />
      </div>

      <div>
        <SubHeading>Participants</SubHeading>
        <p className="mb-2 text-xs text-ink-soft">Who took part. These are recorded facts, not signatures.</p>
        <datalist id={`${id}-roles`}>
          {ROLE_SUGGESTIONS.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
        <div className="space-y-2">
          {participants.map((p, i) => (
            <div key={i} className="flex flex-wrap gap-2 rounded-lg border border-line bg-white p-2">
              <label className="sr-only" htmlFor={`${id}-pn-${i}`}>
                Participant {i + 1} name
              </label>
              <TextInput
                id={`${id}-pn-${i}`}
                className="min-w-[10rem] flex-1"
                placeholder="Name"
                value={p.name}
                onChange={(e) => setParticipants((list) => list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              />
              <label className="sr-only" htmlFor={`${id}-pr-${i}`}>
                Participant {i + 1} role
              </label>
              <TextInput
                id={`${id}-pr-${i}`}
                className="min-w-[10rem] flex-1"
                placeholder="Role at the meeting"
                list={`${id}-roles`}
                value={p.role}
                onChange={(e) => setParticipants((list) => list.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))}
              />
              <Button variant="ghost" onClick={() => setParticipants((list) => list.filter((_, j) => j !== i))} aria-label={`Remove participant ${i + 1}`}>
                Remove
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setParticipants((list) => [...list, { name: '', role: '' }])}>
            Add a participant
          </Button>
          {quickAdds.map((q) => (
            <Button key={`${q.name}-${q.role}`} variant="secondary" onClick={() => addParticipant(q)}>
              Add {q.name} ({q.role})
            </Button>
          ))}
        </div>
      </div>

      <div>
        <SubHeading>RS visit before authorisation</SubHeading>
        <p className="mb-2 text-xs text-ink-soft">Class B has no routine PA/RS visit, but the RS may require one (CORE-035). If Yes, authorisation waits for the recorded visit.</p>
        <YesNo name={`${id}-visit`} value={visit} onChange={setVisit} yesLabel="Yes, a visit is needed" noLabel="No visit needed" />
      </div>

      <Field label="Notes" htmlFor={`${id}-notes`} hint="Optional.">
        <TextArea id={`${id}-notes`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// return_for_revision (RS)
// -----------------------------------------------------------------------------
function ReturnPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const cmd = useGuardedCommand('return_for_revision')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const [reason, setReason] = useState('')
  const [disposition, setDisposition] = useState<'minor' | 'significant' | null>(null)
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  const missing: string[] = []
  if (!nonBlank(reason)) missing.push('the reason')
  if (!disposition) missing.push('whether the change is minor or significant')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={`I return revision ${revision.revision_number} to the applicant for the reason above. Its review and approvals stay with revision ${revision.revision_number}; the next revision must be reviewed again.`}
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign and return"
      onCancel={onCancel}
      onSign={() => {
        if (!disposition) return
        void attempt(
          () => cmd.run({ permit_id: bundle.permit.id, revision_id: revision.id, reason, change_disposition: disposition }, seen.version),
          onDone,
          seen,
        )
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <Field label="What must change, and why" required htmlFor={`${id}-reason`}>
        <TextArea id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div>
        <SubHeading>Size of the change</SubHeading>
        <RadioGroup
          name={`${id}-disp`}
          value={disposition}
          onChange={setDisposition}
          options={[
            { value: 'minor', label: 'Minor change', hint: 'A correction that does not change the scope, location, class or hazards.' },
            { value: 'significant', label: 'Significant change', hint: 'Scope, location, class, hazards or documents change; everything is assessed again.' },
          ]}
        />
      </div>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// recall_permit_application (PA, 0019): take back a submitted application before the
// permit meeting reviews it, for example to correct a wrongly named Permit Holder.
// -----------------------------------------------------------------------------
function RecallPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const cmd = useGuardedCommand('recall_permit_application')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const [reason, setReason] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  const missing: string[] = []
  if (reason.trim().length < 3) missing.push('what you need to correct')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={`I recall revision ${revision.revision_number} of this application to correct it. It has not been reviewed and nothing is authorised; I will submit it again once corrected.`}
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign and recall"
      onCancel={onCancel}
      onSign={() => {
        void attempt(() => cmd.run({ permit_id: bundle.permit.id, revision_id: revision.id, reason: reason.trim() }, seen.version), onDone, seen)
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <p className="text-sm text-ink-soft">
        You can recall the application until the permit meeting reviews it. It then comes back to you as a returned application:
        correct it, then submit it for review again.
      </p>
      <Field label="What you need to correct" required htmlFor={`${id}-reason`} hint="For example: the wrong Permit Holder was named.">
        <TextArea id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// record_oac_agreement (OAC)
// -----------------------------------------------------------------------------
function OacPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const registryQ = useRegistry()
  const cmd = useGuardedCommand('record_oac_agreement')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const fixedArea = argString(action.args, 'affected_site_area_id')
  const reqs = revision ? oacRequirements(revision) : []
  const [areaId, setAreaId] = useState<string>(fixedArea ?? (reqs.length === 1 ? reqs[0].detail.site_area_id : ''))
  const [controls, setControls] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  const lookup = makeLookup(registryQ.data, bundle)
  const areaName = (aid: string) => reqs.find((r) => r.detail.site_area_id === aid)?.detail.area?.name ?? lookup.area(aid)?.name ?? 'Unknown area'
  const agreed = (aid: string) => bundle.approvals.some((a) => a.revision_id === revision.id && a.approval_kind === 'oac_agreement' && a.affected_site_area?.id === aid)
  const missing: string[] = []
  if (!areaId) missing.push('the affected area')
  if (!nonBlank(controls)) missing.push('the added controls (write "None" if there are none)')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={`As a licensed Responsible Supervisor for ${areaId ? areaName(areaId) : 'the affected area'}, I reviewed how this work affects my area and agree to revision ${revision.revision_number} with the added controls above.`}
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign the agreement"
      onCancel={onCancel}
      onSign={() => {
        if (!areaId) return
        void attempt(
          () => cmd.run({ permit_id: bundle.permit.id, revision_id: revision.id, affected_site_area_id: areaId, added_controls: controls }, seen.version),
          onDone,
          seen,
        )
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <p className="text-sm text-ink">
        This work affects, or is affected by, another area. Its custodian reviews the hazards and adds any controls needed before the permit can be authorised.
        <RuleRefs ruleIds={['CORE-047']} />
      </p>
      {fixedArea ? (
        <KeyValue items={[['Affected area', `${areaName(fixedArea)}${agreed(fixedArea) ? ' - agreement already recorded' : ''}`]]} />
      ) : (
        <Field label="Affected area" required htmlFor={`${id}-area`}>
          <Select
            id={`${id}-area`}
            value={areaId}
            placeholder={reqs.length ? 'Choose the area' : 'No affected areas on this revision'}
            options={reqs.map((r) => ({ value: r.detail.site_area_id, label: `${areaName(r.detail.site_area_id)}${agreed(r.detail.site_area_id) ? ' - already agreed' : ''}` }))}
            onChange={(e) => setAreaId(e.target.value)}
          />
        </Field>
      )}
      <ScopeSummary revision={revision} />
      <Field label="Controls you add for your area" required htmlFor={`${id}-controls`} hint='Write "None" if no extra controls are needed.'>
        <TextArea id={`${id}-controls`} value={controls} onChange={(e) => setControls(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// record_pc_approval (PC)
// -----------------------------------------------------------------------------
function PcApprovalPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const cmd = useGuardedCommand('record_pc_approval')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const [notes, setNotes] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={`As Production Coordinator, I approve revision ${revision.revision_number} of this permit. This approval does not authorise the work; the Responsible Supervisor still authorises it.`}
      declared={declared}
      onDeclared={setDeclared}
      missing={nonBlank(notes) ? [] : ['your notes']}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign the approval"
      onCancel={onCancel}
      onSign={() => void attempt(() => cmd.run({ permit_id: bundle.permit.id, revision_id: revision.id, notes }, seen.version), onDone, seen)}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <Banner tone="info">
        Class B work in this release has no trigger that needs Production Coordinator approval (whether full or hydrocarbon isolation needs one is open, DEC-CORE-006). A PC approval is recorded against this
        revision only and does not replace RS authorisation.
        <RuleRefs ruleIds={['CORE-048']} decisionIds={['DEC-CORE-006']} />
      </Banner>
      <ScopeSummary revision={revision} />
      <Field label="Notes" required htmlFor={`${id}-notes`} hint="What you approved and any conditions.">
        <TextArea id={`${id}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// record_worksite_examination (PA or RS, before authorisation)
// -----------------------------------------------------------------------------
function ExaminationPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const clock = useServerClock()
  const cmd = useGuardedCommand('record_worksite_examination')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const [observedAt, setObservedAt] = useState('')
  // The exact time behind "Use the time now" (the input itself shows whole minutes).
  const [pickedNow, setPickedNow] = useState<string | null>(null)
  const [findings, setFindings] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  const review = bundle.reviews
    .filter((r) => r.revision_id === revision.id && r.review_kind === 'meeting_review')
    .sort((a, b) => b.attestation.server_signed_at.localeCompare(a.attestation.server_signed_at))[0]
  const missing: string[] = []
  if (!observedAt) missing.push('when you examined the worksite')
  if (!nonBlank(findings)) missing.push('what you found')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration="I examined the worksite myself at the time stated, before authorisation, and recorded what I found."
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign the examination"
      onCancel={onCancel}
      onSign={() => {
        const observed = inputTimeArg(observedAt, pickedNow)
        if (!observed) return
        void attempt(
          () =>
            cmd.run(
              { permit_id: bundle.permit.id, revision_id: revision.id, exam_kind: 'pa_rs_pre_authorisation', reported_observed_at: observed, findings },
              seen.version,
            ),
          onDone,
          seen,
        )
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <p className="text-sm text-ink">
        {review
          ? review.rs_pre_authorisation_visit_required
            ? 'The permit meeting required this visit before authorisation.'
            : 'The permit meeting did not require this visit; recording it is still allowed.'
          : 'The permit meeting has not reviewed this revision yet.'}
      </p>
      <Field
        label="When you examined the worksite"
        required
        htmlFor={`${id}-at`}
        hint={`Gulf Standard Time (Muscat, UTC+4). It cannot be in the future, or before this revision was submitted (${fmtDateTime(revision.sealed_at)}).`}
      >
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-[16rem] flex-1">
            <DateTimeInput id={`${id}-at`} name="When you examined the worksite" value={observedAt} onChange={setObservedAt} />
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              const now = new Date(clock()).toISOString()
              setPickedNow(now)
              setObservedAt(toLocalInput(now))
            }}
          >
            Use the time now
          </Button>
        </div>
      </Field>
      <Field label="What you found" required htmlFor={`${id}-findings`}>
        <TextArea id={`${id}-findings`} value={findings} onChange={(e) => setFindings(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// record_supporting_document (PA)
// -----------------------------------------------------------------------------
function RecordDocumentPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const cmd = useGuardedCommand('record_supporting_document')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const reqs = revision ? docRequirements(revision) : []
  const [requirementId, setRequirementId] = useState(argString(action.args, 'requirement_id') ?? (reqs.length === 1 ? reqs[0].id : ''))
  const [identifier, setIdentifier] = useState('')
  const [rev, setRev] = useState('')
  const [issuer, setIssuer] = useState('')
  const [issuedAt, setIssuedAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [scopeNote, setScopeNote] = useState('')
  const [evidence, setEvidence] = useState('')
  const [declared, setDeclared] = useState(false)
  if (!revision) return <MissingRevision onCancel={onCancel} />
  const req = reqs.find((r) => r.id === requirementId) ?? null
  const records = bundle.supporting_records.filter((r) => r.revision_id === revision.id)
  const procedures = revision.tasks
    .map(taskFromRevision)
    .filter((t) => t.procedure)
    .sort((a, b) => a.sequence - b.sequence)
  const missing: string[] = []
  if (!req) missing.push('which required document this is')
  if (!nonBlank(identifier)) missing.push('the document identifier')
  if (!nonBlank(rev)) missing.push('its revision')
  if (!nonBlank(issuer)) missing.push('who issued it')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration="I entered these details exactly as they appear on the document. This records the document against the permit; it is not a verification."
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign and record"
      onCancel={onCancel}
      onSign={() => {
        if (!req) return
        void attempt(
          () =>
            cmd.run(
              {
                permit_id: bundle.permit.id,
                revision_id: revision.id,
                requirement_id: req.id,
                record_kind: req.detail.kind,
                external_identifier: identifier,
                external_revision: rev,
                issuer,
                reported_issued_at: issuedAt || null,
                reported_expires_at: expiresAt || null,
                scope_note: nonBlank(scopeNote) ? scopeNote : null,
                evidence_reference: nonBlank(evidence) ? evidence : null,
              },
              seen.version,
            ),
          onDone,
          seen,
        )
      }}
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <Field label="Required document" required htmlFor={`${id}-req`}>
        <Select
          id={`${id}-req`}
          value={requirementId}
          placeholder={reqs.length ? 'Choose the requirement' : 'No supporting documents are required on this revision'}
          options={reqs.map((r) => {
            const n = records.filter((x) => x.requirement_id === r.id)
            return { value: r.id, label: `${r.detail.kind}${r.detail.reason ? ` - ${r.detail.reason}` : ''}${n.length ? ` (${n.length} recorded)` : ''}` }
          })}
          onChange={(e) => setRequirementId(e.target.value)}
        />
      </Field>
      {req ? (
        <KeyValue
          items={[
            ['Kind (from the requirement)', req.detail.kind],
            ['Already recorded', records.filter((x) => x.requirement_id === req.id).map(recordLabel).join('; ') || 'Nothing yet'],
          ]}
        />
      ) : null}
      {procedures.length > 0 ? (
        <div className="rounded-lg border border-line bg-paper p-3">
          <p className="text-sm font-semibold text-ink">Task procedures on this revision</p>
          <p className="mb-2 text-xs text-ink-soft">
            A task procedure counts as verified only when a verified document has exactly the same identifier, revision and issuer. Copy them only if they match the
            document in front of you.
          </p>
          <ul className="space-y-2">
            {procedures.map((t) => (
              <li key={t.sequence} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  Task {t.sequence}: {procedureText(t.procedure)}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIdentifier(t.procedure?.identity ?? '')
                    setRev(t.procedure?.revision ?? '')
                    setIssuer(t.procedure?.issuer ?? '')
                  }}
                >
                  Copy these details
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="grid gap-x-3 sm:grid-cols-3">
        <Field label="Document identifier" required htmlFor={`${id}-ident`}>
          <TextInput id={`${id}-ident`} value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
        </Field>
        <Field label="Revision" required htmlFor={`${id}-rev`}>
          <TextInput id={`${id}-rev`} value={rev} onChange={(e) => setRev(e.target.value)} />
        </Field>
        <Field label="Issued by" required htmlFor={`${id}-issuer`}>
          <TextInput id={`${id}-issuer`} value={issuer} onChange={(e) => setIssuer(e.target.value)} />
        </Field>
      </div>
      <div className="grid gap-x-3 sm:grid-cols-2">
        <Field label="Issue date" htmlFor={`${id}-issued`} hint="As stated on the document. Optional.">
          <DateInput id={`${id}-issued`} name="Issue date" value={issuedAt} onChange={setIssuedAt} />
        </Field>
        <Field label="Expiry date" htmlFor={`${id}-expires`} hint="As stated on the document. Optional.">
          <DateInput id={`${id}-expires`} name="Expiry date" value={expiresAt} onChange={setExpiresAt} />
        </Field>
      </div>
      <Field label="Scope note" htmlFor={`${id}-scope`} hint="What the document covers. Optional.">
        <TextArea id={`${id}-scope`} rows={2} value={scopeNote} onChange={(e) => setScopeNote(e.target.value)} />
      </Field>
      <Field label="Evidence reference" htmlFor={`${id}-evidence`} hint="Where the document can be seen, for example a document-system number. Optional.">
        <TextInput id={`${id}-evidence`} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// verify_supporting_document (type-specific verifier)
// -----------------------------------------------------------------------------
function VerifyDocumentPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const policyQ = usePolicy(bundle.policy.version_id)
  const cmd = useGuardedCommand('verify_supporting_document')
  const seen = useSeenVersion(bundle)
  const currentRecords = bundle.supporting_records.filter((r) => r.revision_id === bundle.permit.current_revision_id)
  const prefilled = argString(action.args, 'supporting_record_id')
  const candidates = currentRecords.filter((r) => r.status === 'recorded' || r.id === prefilled)
  const [recordId, setRecordId] = useState(prefilled ?? (candidates.length === 1 ? candidates[0].id : ''))
  const [findings, setFindings] = useState('')
  const [declared, setDeclared] = useState(false)
  const record = bundle.supporting_records.find((r) => r.id === recordId) ?? null
  const routes = record ? verifierRolesFor(policyQ.data, record.record_kind) : null
  const routeParam = findParam(policyQ.data, 'supporting_documents.routes')
  const tasks = bundle.revisions.find((r) => r.id === record?.revision_id)?.tasks.map(taskFromRevision).filter((t) => t.procedure) ?? []
  const missing: string[] = []
  if (!record) missing.push('which document you checked')
  if (!nonBlank(findings)) missing.push('what you checked and found')
  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration="I checked this document against its source - issuer, revision, scope and validity - and confirm it is the correct, current document for this permit."
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign the verification"
      onCancel={onCancel}
      onSign={() => void attempt(() => cmd.run({ permit_id: bundle.permit.id, supporting_record_id: recordId, findings }, seen.version), onDone, seen)}
    >
      <Field label="Document" required htmlFor={`${id}-rec`} hint="Documents on the current revision that are recorded and not yet verified.">
        <Select
          id={`${id}-rec`}
          value={recordId}
          placeholder={candidates.length ? 'Choose the document' : 'Nothing is waiting for verification'}
          options={candidates.map((r) => ({ value: r.id, label: recordLabel(r) }))}
          onChange={(e) => setRecordId(e.target.value)}
        />
      </Field>
      {record ? (
        <>
          <RecordSummary record={record} />
          {routes === null ? null : routes.length === 0 ? (
            <Banner tone="bad" title="No verification route for this kind">
              The governing profile has no verification route for a {record.record_kind}. The server will refuse the verification and authorisation stays blocked.
              <RuleRefs ruleIds={routeParam?.rule_ids ?? []} decisionIds={routeParam?.decision_ids ?? ['DEC-GOV-010']} />
            </Banner>
          ) : (
            <PolicyNote param={routeParam}>
              Verifier for a {record.record_kind}: {routes.map((r) => ROLES[r as keyof typeof ROLES]?.label ?? r).join(' or ')}.
            </PolicyNote>
          )}
          {tasks.length > 0 ? (
            <div className="rounded-lg border border-line bg-paper p-3 text-sm">
              <p className="font-semibold text-ink">Task procedures on that revision</p>
              <ul className="mt-1 list-inside list-disc">
                {tasks.map((t) => (
                  <li key={t.sequence}>
                    Task {t.sequence}: {procedureText(t.procedure)}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ink-soft">The server links a verified document to a task procedure only when identifier, revision and issuer match exactly.</p>
            </div>
          ) : null}
        </>
      ) : null}
      <Field label="What you checked and found" required htmlFor={`${id}-findings`}>
        <TextArea id={`${id}-findings`} value={findings} onChange={(e) => setFindings(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// withdraw_supporting_document (versionless)
// -----------------------------------------------------------------------------
function WithdrawDocumentPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const cmd = useGuardedCommand('withdraw_supporting_document')
  const prefilled = argString(action.args, 'supporting_record_id')
  const candidates = bundle.supporting_records.filter((r) => r.status !== 'withdrawn' || r.id === prefilled)
  const [recordId, setRecordId] = useState(prefilled ?? '')
  const [reason, setReason] = useState('')
  const [declared, setDeclared] = useState(false)
  const record = bundle.supporting_records.find((r) => r.id === recordId) ?? null
  const revisionNo = (rid: string) => bundle.revisions.find((r) => r.id === rid)?.revision_number
  const missing: string[] = []
  if (!record) missing.push('which document to withdraw')
  if (!nonBlank(reason)) missing.push('the reason')
  return (
    <PanelFrame
      action={action}
      declaration="I withdraw this document from use on this permit for the reason above."
      declared={declared}
      onDeclared={setDeclared}
      missing={missing}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign and withdraw"
      danger
      onCancel={onCancel}
      onSign={() => void attempt(() => cmd.run({ permit_id: bundle.permit.id, supporting_record_id: recordId, reason }), onDone)}
    >
      <Banner tone="bad" title="Dependent work stops at once">
        Withdrawing takes effect immediately. Any work that relies on this document must stop, and the permit cannot be used for work until the server shows the
        requirement met again. Withdrawals cannot be undone; a replacement document is recorded and verified as a new record.
        <RuleRefs ruleIds={['CORE-033', 'CORE-053']} />
      </Banner>
      <Field label="Document" required htmlFor={`${id}-rec`}>
        <Select
          id={`${id}-rec`}
          value={recordId}
          placeholder={candidates.length ? 'Choose the document' : 'No documents to withdraw'}
          options={candidates.map((r) => ({ value: r.id, label: `${recordLabel(r)}${revisionNo(r.revision_id) !== undefined ? ` (revision ${revisionNo(r.revision_id)})` : ''}` }))}
          onChange={(e) => setRecordId(e.target.value)}
        />
      </Field>
      {record ? <RecordSummary record={record} /> : null}
      <Field label="Why it is withdrawn" required htmlFor={`${id}-reason`}>
        <TextArea id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// authorise_permit (RS)
// -----------------------------------------------------------------------------
function StatusRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 p-2 text-sm">
      <span className="text-ink">{label}</span>
      <span className="flex flex-wrap justify-end gap-1">{children}</span>
    </li>
  )
}

function AuthorisePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const id = useId()
  const registryQ = useRegistry()
  const policyQ = usePolicy(bundle.policy.version_id)
  const nowMs = useServerNow()
  const cmd = useGuardedCommand('authorise_permit')
  const seen = useSeenVersion(bundle)
  const revision = revisionFor(bundle, action)
  const content = revision ? normaliseDraftFields(revision.content) : null
  const [fromInput, setFromInput] = useState(toLocalInput(content?.requested_from ?? null))
  const [toInput, setToInput] = useState(toLocalInput(content?.requested_to ?? null))
  const [declared, setDeclared] = useState(false)
  if (!revision || !content) return <MissingRevision onCancel={onCancel} />

  const lookup = makeLookup(registryQ.data, bundle)
  const review = bundle.reviews
    .filter((r) => r.revision_id === revision.id && r.review_kind === 'meeting_review' && r.outcome === 'accepted')
    .sort((a, b) => b.attestation.server_signed_at.localeCompare(a.attestation.server_signed_at))[0]
  const visit = bundle.examinations.find((x) => x.revision_id === revision.id && x.exam_kind === 'pa_rs_pre_authorisation')
  const oacReqs = oacRequirements(revision)
  const docReqs = docRequirements(revision)
  const records = bundle.supporting_records.filter((r) => r.revision_id === revision.id)
  const procTasks = [...revision.tasks].sort((a, b) => a.sequence - b.sequence).filter((t) => t.procedure_verification_status !== 'not_applicable')
  const maxDaysParam = findParam(policyQ.data, 'class_b.authorisation_max_days')
  const maxDays = paramNumber(maxDaysParam)
  const fromArg = windowArg(fromInput, content.requested_from)
  const toArg = windowArg(toInput, content.requested_to)
  const startPassed = content.requested_from ? Date.parse(content.requested_from) < nowMs : false
  const span = windowSpan(fromInput ? (fromLocalInput(fromInput) ?? null) : content.requested_from, toInput ? (fromLocalInput(toInput) ?? null) : content.requested_to)

  return (
    <PanelFrame
      action={action}
      seen={seen}
      declaration={AUTHORISE_DECLARATION}
      declared={declared}
      onDeclared={setDeclared}
      missing={[]}
      error={cmd.error}
      pending={cmd.isPending}
      signLabel="Sign and authorise"
      onCancel={onCancel}
      onSign={() =>
        void attempt(
          () => cmd.run({ permit_id: bundle.permit.id, revision_id: revision.id, authorisation_from: fromArg, authorisation_to: toArg }, seen.version),
          onDone,
          seen,
        )
      }
    >
      <RevisionLine revision={revision} bundle={bundle} />
      <ScopeSummary revision={revision} />

      <div>
        <SubHeading>What the records show now</SubHeading>
        <ul className="divide-y divide-line rounded-lg border border-line bg-white">
          <StatusRow label="Permit meeting review">
            {review ? <Badge tone="ok">Accepted {fmtDateTime(review.attestation.server_signed_at)}</Badge> : <Badge tone="bad">No accepted review</Badge>}
          </StatusRow>
          <StatusRow label="RS visit before authorisation">
            {!review ? (
              <Badge tone="neutral">Decided at the meeting</Badge>
            ) : review.rs_pre_authorisation_visit_required ? (
              visit ? (
                <Badge tone="ok">Required - recorded {fmtDateTime(visit.reported_observed_at)}</Badge>
              ) : (
                <Badge tone="bad">Required - not recorded</Badge>
              )
            ) : (
              <Badge tone="neutral">Not required by the meeting</Badge>
            )}
          </StatusRow>
          {oacReqs.map((r) => {
            const agreement = bundle.approvals.find(
              (a) => a.revision_id === revision.id && a.approval_kind === 'oac_agreement' && a.affected_site_area?.id === r.detail.site_area_id,
            )
            return (
              <StatusRow key={r.id} label={`Custodian agreement: ${r.detail.area?.name ?? lookup.area(r.detail.site_area_id)?.name ?? 'affected area'}`}>
                {agreement ? <Badge tone="ok">Agreed by {agreement.attestation.signer.display_name}</Badge> : <Badge tone="bad">Awaiting agreement</Badge>}
              </StatusRow>
            )
          })}
          {docReqs.map((r) => {
            const recs = records.filter((x) => x.requirement_id === r.id)
            return (
              <StatusRow key={r.id} label={`Document: ${r.detail.kind}`}>
                {recs.length === 0 ? (
                  <Badge tone="bad">No record</Badge>
                ) : (
                  recs.map((x) => (
                    <StatusBadge
                      key={x.id}
                      status={{ label: `${x.external_identifier} rev ${x.external_revision}: ${RECORD_STATUS[x.status]?.label ?? x.status}`, tone: RECORD_STATUS[x.status]?.tone ?? 'neutral' }}
                    />
                  ))
                )}
              </StatusRow>
            )
          })}
          {procTasks.map((t) => (
            <StatusRow key={t.id} label={`Task ${t.sequence} procedure`}>
              <StatusBadge status={PROCEDURE_STATUS[t.procedure_verification_status] ?? { label: t.procedure_verification_status, tone: 'neutral' }} />
            </StatusRow>
          ))}
        </ul>
        <p className="mt-1 text-xs text-ink-soft">The server checks all of these again when you sign, together with your qualification and that you are not part of this work.</p>
      </div>

      <div>
        <SubHeading>Authorisation window</SubHeading>
        <p className="mb-2 text-xs text-ink-soft">
          Starts from the requested window. You may shorten it; it cannot run past the requested end.
          {startPassed ? ' The requested start has passed, so the authorisation starts at the server time when you sign.' : ''}
        </p>
        <div className="grid gap-x-3 sm:grid-cols-2">
          <Field label="From" htmlFor={`${id}-from`}>
            <DateTimeInput id={`${id}-from`} name="Authorised from" value={fromInput} onChange={setFromInput} />
          </Field>
          <Field label="To" htmlFor={`${id}-to`}>
            <DateTimeInput id={`${id}-to`} name="Authorised to" value={toInput} onChange={setToInput} />
          </Field>
        </div>
        <p className="text-sm text-ink">
          Requested: <WindowText from={content.requested_from} to={content.requested_to} />
        </p>
        {span ? <p className={span.ms > 0 ? 'mt-1 text-sm text-ink' : 'mt-1 text-sm font-semibold text-bad'}>{span.ms > 0 ? `Length: ${span.text}.` : '! The end must be after the start.'}</p> : null}
        {maxDays !== null ? <PolicyNote param={maxDaysParam}>Class B authorisation is at most {maxDays} days.</PolicyNote> : null}
      </div>

      <Banner tone="info">
        Authorising does not start work. The Operations Manager approves the authorised permit first, where the policy requires it. Then each day the RS pre-issues the cycle, an RS validates it and the Permit Holder accepts it before work may proceed.
        <RuleRefs ruleIds={['CORE-049', 'CORE-055', 'CORE-057']} />
      </Banner>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
export const applicationActionPanels: ActionPanelMap = {
  revise_permit_draft: ReviseDraftPanel,
  submit_permit: SubmitPanel,
  record_meeting_review: MeetingReviewPanel,
  return_for_revision: ReturnPanel,
  recall_permit_application: RecallPanel,
  record_oac_agreement: OacPanel,
  record_pc_approval: PcApprovalPanel,
  record_worksite_examination: ExaminationPanel,
  record_supporting_document: RecordDocumentPanel,
  verify_supporting_document: VerifyDocumentPanel,
  withdraw_supporting_document: WithdrawDocumentPanel,
  authorise_permit: AuthorisePanel,
}
